import React from "react";
import {
  FileText,
  Calendar,
  Scale,
  Building,
  User,
  Users,
  ShieldAlert,
  Laptop,
  CreditCard,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";

/**
 * Componente de spinner animado para loading
 */
function LoadingSpinner({ text }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 gap-3">
      <div className="relative w-10 h-10">
        {/* Círculo externo rotacionando */}
        <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
      </div>
      {text && (
        <div className="text-gray-600 text-sm font-medium">{text}</div>
      )}
    </div>
  );
}

/**
 * Componente para exibir dados CNJ de capa e partes do processo
 */
export default function ProcessCNJDataPanel({
  cnjData,
  loading,
  cnjNotFound = false,
  isExpanded,
  onToggle,
  cadastroMeta = null,
}) {
  const renderCnjBody = () => {
    if (loading) {
      return <LoadingSpinner text="Carregando dados do CNJ..." />;
    }

    if (cnjNotFound) {
      return (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-yellow-800">
              Dados do CNJ não encontrados para este processo
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Não há informações do CNJ vinculadas a este processo no momento.
            </p>
          </div>
        </div>
      );
    }

    if (!cnjData) {
      return <LoadingSpinner text="Buscando dados do processo..." />;
    }

    return null;
  };

  const cnjPlaceholder = renderCnjBody();

  // Agrupar participantes por polo
  const participantesPorPolo =
    cnjData?.participants?.reduce((acc, participant) => {
      const polo = participant.poleName || "Outros";
      if (!acc[polo]) {
        acc[polo] = [];
      }
      acc[polo].push(participant);
      return acc;
    }, {}) || {};

  const poloSortKey = (polo) => {
    const normalized = (polo || "").toLowerCase();
    if (normalized.includes("ativa") || normalized.includes("ativo")) return 0;
    if (normalized.includes("passiva") || normalized.includes("passivo")) return 1;
    return 2;
  };

  const sortParticipants = (participantes) => {
    return [...participantes].sort((a, b) => {
      const aAdv = a.typeName?.toLowerCase().includes("advogado");
      const bAdv = b.typeName?.toLowerCase().includes("advogado");
      if (aAdv && !bAdv) return 1;
      if (!aAdv && bAdv) return -1;
      return 0;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Header clicável */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-2 min-w-0 text-left">
          <Building className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              Informações do Processo
            </h3>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {loading
                  ? "Carregando dados do CNJ..."
                  : cnjNotFound
                    ? "Situação, classificação e capa — CNJ indisponível"
                    : cnjData
                      ? "Situação, classificação, capa e partes — clique para expandir"
                      : "Situação, classificação e capa — clique para expandir"}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" aria-hidden />
        )}
      </button>

      {/* Conteúdo expandível */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          {cadastroMeta && (
            <div className="pt-4 pb-6 mb-6 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Status e classificação do cadastro
              </p>
              {cadastroMeta}
            </div>
          )}

          {cnjPlaceholder || (
            <>
              {/* Dados da Capa */}
              <div className="mb-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem
                    icon={<FileText className="w-4 h-4" />}
                    label="Número CNJ"
                    value={cnjData.numberFormatted || cnjData.number || "N/A"}
                  />
                  <InfoItem
                    icon={<Building className="w-4 h-4" />}
                    label="Sistema"
                    value={cnjData.systemName || "N/A"}
                  />
                  <InfoItem
                    icon={<Scale className="w-4 h-4" />}
                    label="Vara/Tribunal"
                    value={cnjData.courtName || "N/A"}
                  />
                  <InfoItem
                    icon={<Building className="w-4 h-4" />}
                    label="Órgão"
                    value={cnjData.organName || "N/A"}
                  />
                  <InfoItem
                    icon={<Scale className="w-4 h-4" />}
                    label="Tipo de Justiça"
                    value={cnjData.justiceTypeName || "N/A"}
                  />
                  <InfoItem
                    icon={<Building className="w-4 h-4" />}
                    label="Estado/Província"
                    value={cnjData.provinceName || "N/A"}
                  />
                  <InfoItem
                    icon={<Building className="w-4 h-4" />}
                    label="Cidade"
                    value={cnjData.cityName || "N/A"}
                  />
                  <InfoItem
                    icon={<FileText className="w-4 h-4" />}
                    label="Instância"
                    value={
                      cnjData.instanceName ||
                      `${cnjData.instance}ª instância` ||
                      "N/A"
                    }
                  />
                  <InfoItem
                    icon={<Calendar className="w-4 h-4" />}
                    label="Data de Distribuição"
                    value={formatDate(cnjData.distributed)}
                  />
                  <InfoItem
                    icon={<FileText className="w-4 h-4" />}
                    label="Situação"
                    value={cnjData.situation || "N/A"}
                    highlight={cnjData.situation === "Ativo" ? "green" : "gray"}
                  />
                  <InfoItem
                    icon={<Laptop className="w-4 h-4" />}
                    label="Processo Digital"
                    value={cnjData.isDigital === "true" ? "Sim" : "Não"}
                    highlight={cnjData.isDigital === "true" ? "blue" : "gray"}
                  />
                  <InfoItem
                    icon={<ShieldAlert className="w-4 h-4" />}
                    label="Nível de Sigilo"
                    value={cnjData.secretLevel || "N/A"}
                  />
                </div>
              </div>

              {/* Partes do Processo */}
              {cnjData.participants && cnjData.participants.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2 pb-3 border-b border-gray-100">
                    <Users className="w-4 h-4 text-gray-600" />
                    Partes do Processo
                  </h4>

                  <div className="space-y-3">
                    {Object.entries(participantesPorPolo)
                      .sort(([poloA], [poloB]) => poloSortKey(poloA) - poloSortKey(poloB))
                      .map(([polo, participantes]) => {
                        const isPoloAtivo =
                          polo.toLowerCase().includes("ativa") ||
                          polo.toLowerCase().includes("ativo");
                        const orderedParticipants = sortParticipants(participantes);

                        return (
                          <div key={polo} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${
                                  isPoloAtivo
                                    ? "bg-green-100 text-green-700 border-green-200"
                                    : "bg-red-100 text-red-700 border-red-200"
                                }`}
                              >
                                {polo}
                              </span>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                              {orderedParticipants.map((participant) => (
                                <ParticipantCard
                                  key={participant.id}
                                  participant={participant}
                                  isPoloAtivo={isPoloAtivo}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function InfoItem({ icon, label, value, highlight }) {
  const getHighlightClass = () => {
    switch (highlight) {
      case "green":
        return "text-green-700";
      case "blue":
        return "text-blue-700";
      case "red":
        return "text-red-700";
      case "yellow":
        return "text-yellow-700";
      default:
        return "text-gray-900";
    }
  };

  return (
    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="text-gray-600 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-0.5">
          {label}
        </div>
        <div className={`text-sm font-medium ${getHighlightClass()} truncate`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function ParticipantCard({ participant, isPoloAtivo }) {
  const isAdvogado = participant.typeName?.toLowerCase().includes("advogado");

  return (
    <div
      className={`bg-white rounded-lg border p-4 ${
        isAdvogado
          ? "border-gray-100 ml-4 border-l-2 border-l-gray-300"
          : "border-gray-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 ${isPoloAtivo ? "text-green-600" : "text-red-600"}`}
        >
          {isAdvogado ? (
            <Scale className="w-4 h-4 text-gray-500" />
          ) : (
            <User className="w-4 h-4" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            {isAdvogado ? "Advogado" : "Parte"}
          </p>
          <h5
            className={`text-gray-900 font-medium ${
              isAdvogado ? "text-sm" : "text-base"
            }`}
          >
            {participant.participantName}
          </h5>
          {participant.typeName && !isAdvogado && (
            <span
              className={`inline-flex items-center mt-2 px-2 py-0.5 rounded-md text-xs font-medium border ${
                isPoloAtivo
                  ? "bg-green-100 text-green-700 border-green-200"
                  : "bg-red-100 text-red-700 border-red-200"
              }`}
            >
              {participant.typeName}
            </span>
          )}

          {participant.documents && participant.documents.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {participant.documents.map((doc) => (
                <DocumentBadge key={doc.id} document={doc} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentBadge({ document }) {
  const getDocTypeClass = (type) => {
    if (type?.includes("CPF"))
      return "bg-blue-100 text-blue-700 border-blue-200";
    if (type?.includes("CNPJ"))
      return "bg-purple-100 text-purple-700 border-purple-200";
    if (type?.includes("OAB"))
      return "bg-green-100 text-green-700 border-green-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border ${getDocTypeClass(document.type)}`}
    >
      <CreditCard className="w-3 h-3" />
      <span className="font-semibold">{document.type}:</span>
      <span>{document.value}</span>
    </div>
  );
}
